import ChevronDown from '@icons/ChevronDown.svg';
import classNames from 'classnames';
import { ChangeEvent, FC, KeyboardEvent, MouseEvent, SVGProps, useRef, useState } from 'react';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import { DropdownMenu } from '../../DropdownMenu';
type Option = { label: string; value: string };

export interface Props {
  /**
   * Label of the dropdown menu
   */
  label?: string;
  /**
   * Hint text of the dropdown menu
   */
  hintText?: string;
  /**
   * Placeholder of the dropdown menu. When no option is selected
   */
  placeholder: string;
  /**
   * Type of the dropdown menu
   */
  type?: 'single' | 'multiple';
  /**
   * Options to list in the dropdown
   */
  options: Option[];
  /**
   * Selected value or values of the options
   */
  selectedValues?: string | string[];
  /**
   * Selected value icon
   */
  icon?: FC<SVGProps<SVGElement>>;
  /**
   * Unique name of the dropdown menu
   */
  name: string;
  /**
   * Direction of the dropdown menu
   */
  direction?: 'up' | 'down';
  /**
   * Custom callback when an option's onChange event is fired
   */
  onChange: (value: string) => void;
  /**
   * ClassName
   */
  className?: string;
}

export const Dropdown = ({
  label,
  hintText,
  placeholder,
  name,
  options,
  onChange,
  className,
  selectedValues,
  type = 'single',
  direction = 'down',
  icon: Icon,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const ref = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const menuRef = useClickOutside({
    callback: () => {
      setIsOpen(false);
    },
    listenGlobally: true,
  });

  const buttonHeight = buttonRef.current?.offsetHeight || 0;

  const textValue = (() => {
    if (Array.isArray(selectedValues)) {
      return selectedValues.length
        ? options
            .filter((option) => selectedValues.includes(option.value))
            .map((o) => o.label)
            .join(', ')
        : null;
    } else {
      return options.find((i) => i.value === selectedValues)?.label || null;
    }
  })();

  const handleFocusTrapKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab' && ref.current) {
      const inputs = ref.current.querySelectorAll<HTMLInputElement>(`#${name}_dropdown-menu input`);
      const firstItem = inputs[0];
      const lastItem = inputs[inputs.length - 1];

      if (lastItem && document.activeElement === lastItem && firstItem) {
        event.preventDefault();
        firstItem.focus();
      }
    }
  };

  const handleClosingKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    const dropdownBtn = ref.current?.querySelector<HTMLInputElement>(`#${name}_dropdown-button`);
    if ((event.key === 'Escape' || event.key === 'Enter') && isOpen && dropdownBtn) {
      event.preventDefault();
      dropdownBtn.focus();
      setIsOpen(false);
    }
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    handleClosingKeyPress(event);
    if (type === 'multiple') {
      handleFocusTrapKeyPress(event);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleMouseClick = (e: MouseEvent<HTMLInputElement>) => {
    // Any UIEvent that returns > 0 means it was a mouse click event
    if (e.detail > 0) {
      setIsOpen(false);
    }
  };

  return (
    <div ref={ref} className={classNames(className)}>
      {label && <span className='mb-2 inline-block text-sm text-neutral-text-light'>{label}</span>}

      <button
        ref={buttonRef}
        id={`${name}_dropdown-button`}
        data-cy={`${name.replaceAll(' ', '-')}-btn`}
        className={classNames(
          'w-full',
          'flex items-center justify-between gap-2',
          'rounded-[3px] px-3.5 py-2.5',
          'bg-surface-dark',
          'text-md font-semibold',
          'border border-solid border-neutral-text-medium',
          'hover:border-neutral-text-light hover:text-neutral-text-light',
          {
            'text-neutral-text-medium': !selectedValues,
            'text-neutral-text-light': selectedValues,
          },
        )}
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}
      >
        <span className='flex overflow-hidden text-ellipsis whitespace-nowrap' title={textValue || placeholder}>
          {Icon && <Icon className='mr-2 size-6 shrink-0' />}
          {textValue || placeholder}
        </span>
        <ChevronDown className='h-6 w-6' />
      </button>

      <div
        className='relative'
        onKeyDown={handleKeyPress}
        style={{ top: direction === 'up' ? `calc(-${String(buttonHeight)}px - 0.25rem)` : '0.25rem' }}
      >
        {isOpen && (
          <DropdownMenu
            ref={menuRef}
            name={name}
            options={options}
            selectedValues={selectedValues}
            type={type}
            onChange={handleChange}
            onClick={type === 'single' ? handleMouseClick : undefined}
            className={classNames('absolute w-full', direction === 'up' ? 'bottom-full' : 'bottom-auto')}
          />
        )}
      </div>

      {hintText && <span className='mt-2 inline-block text-sm text-neutral-text-light'>{hintText}</span>}
    </div>
  );
};
